import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * Hook de navigation contextuelle pour le bouton "Retour".
 * Règles:
 *   Marketplace → Dashboard principal (/training)
 *   Dashboard Coach → Marketplace
 *   Fiche Coach → Marketplace
 *   Mes réservations → Marketplace
 *   Session visio → Mes réservations
 *   Admin pages → Admin
 *   Onboarding → Marketplace
 */

const ROUTE_LABELS = {
  '/training': 'Retour au dashboard',
  '/marketplace': 'Retour à l\'espace Coach',
  '/coach/dashboard': 'Retour au dashboard coach',
  '/my-bookings': 'Retour à mes réservations',
  '/my-company-space': 'Retour à mon espace',
  '/admin': 'Retour à l\'admin',
};

const FALLBACK_MAP = {
  '/coach/dashboard': '/marketplace',
  '/my-bookings': '/marketplace',
  '/my-company-space': '/marketplace',
  '/marketplace': '/training',
  '/devenir-coach': '/marketplace',
  '/booking-success': '/my-bookings',
  '/admin/coaches': '/admin',
  '/company/coaching-humain': '/training',
  '/subscription/enterprise/ia': '/subscription/enterprise',
  '/subscription/enterprise/coaching-humain': '/subscription/enterprise',
};

export function useSmartBack() {
  const navigate = useNavigate();
  const location = useLocation();

  const getBackInfo = useCallback(() => {
    const fromRoute = location.state?.from;

    // 🔒 Iter251 — Si un `from` explicite est passé via navigate(), on l'honore.
    if (fromRoute && ROUTE_LABELS[fromRoute]) {
      return { label: ROUTE_LABELS[fromRoute], action: () => navigate(fromRoute) };
    }
    if (fromRoute) {
      // from non référencé dans ROUTE_LABELS → label générique
      return { label: 'Retour', action: () => navigate(fromRoute) };
    }

    // 🔒 Iter251 — Pas de `from` explicite : utiliser l'historique navigateur réel
    // quand disponible (résout "Retour au dashboard" alors que l'utilisateur vient
    // d'une autre page interne). `window.history.length > 1` garantit qu'il y a
    // un vrai historique (accès direct = 1 seule entrée).
    if (typeof window !== 'undefined' && window.history.length > 1) {
      return { label: 'Retour', action: () => window.history.back() };
    }

    const currentPath = location.pathname;

    // Coach profile page → marketplace
    if (currentPath.startsWith('/marketplace/coach/')) {
      return { label: 'Retour à l\'espace Coach', action: () => navigate('/marketplace') };
    }

    // Session page → mes réservations
    if (currentPath.startsWith('/session/')) {
      return { label: 'Retour à mes réservations', action: () => navigate('/my-bookings') };
    }

    // Admin sub-pages → admin
    if (currentPath.startsWith('/admin/')) {
      return { label: 'Retour à l\'admin', action: () => navigate('/admin') };
    }

    // Exact match fallbacks
    for (const [prefix, fallback] of Object.entries(FALLBACK_MAP)) {
      if (currentPath === prefix || currentPath.startsWith(prefix + '/')) {
        const label = ROUTE_LABELS[fallback] || 'Retour';
        return { label, action: () => navigate(fallback) };
      }
    }

    // Default → dashboard
    return { label: 'Retour au dashboard', action: () => navigate('/training') };
  }, [navigate, location]);

  return getBackInfo;
}
